<template>
  <div
    class="cookie-consent-container w-full max-w-full"
    data-target="cookie-consent-container"
  >
    <CookieConsentBanner
      v-if="!hasRespondedToConsent"
      :title="t('cookieConsent.banner.title')"
      :subtitle="t('cookieConsent.banner.subtitle')"
      :description="t('cookieConsent.banner.description')"
      :manage-preferences-label="t('cookieConsent.banner.managePreferencesBtn')"
      :accept-essential-cookies-only-label="
        t('cookieConsent.banner.acceptEssentialsBtn')
      "
      :accept-all-label="t('cookieConsent.banner.acceptAllBtn')"
      @manage-preferences="openCookieSettingsDialog"
      @accept-essential-cookies-only="acceptEssentialCookiesOnly"
      @accept-all="acceptAllCookiesAndCloseSettings"
    />
    <CookieConsentReadyToChatDialog
      v-if="isReadyToChatDialogVisible"
      :title-text="t('cookieConsent.readyToChat.heading')"
      :description-text="t('cookieConsent.readyToChat.bodyText')"
      :cookie-settings-link-text="t('cookieConsent.readyToChat.settingsLink')"
      :confirm-btn-text="t('cookieConsent.readyToChat.enableSupportBtn')"
      :cancel-btn-text="t('cookieConsent.readyToChat.stayPrivateBtn')"
      @close="closeReadyToChatDialog"
      @decline-chat="declineChatAndShowPrivacyRespectedDialog"
      @confirm-chat="confirmChatAndEnableCookies"
      @open-cookie-settings="openCookieSettingsDialog"
    />
    <CookieConsentPrivacyRespectedDialog
      v-if="isPrivacyRespectedDialogVisible"
      :title-text="t('cookieConsent.privacyRespected.heading')"
      :description-text="t('cookieConsent.privacyRespected.bodyText')"
      :support-box-title-text="
        t('cookieConsent.privacyRespected.supportBoxTitle')
      "
      :support-box-email-text="config.public.SUPPORT_EMAIL"
      :support-box-subtitle-text="
        t('cookieConsent.privacyRespected.supportBoxSubtitle')
      "
      :close-btn-text="t('cookieConsent.privacyRespected.closeBtn')"
      @close-dialog="isPrivacyRespectedDialogVisible = false"
    />
    <CookieConsentSettingsDialog
      v-if="isCookieSettingsDialogVisible"
      :title-text="t('cookieConsent.settingsDialog.heading')"
      :intro-text="t('cookieConsent.settingsDialog.bodyText')"
      :accept-all-btn-text="t('cookieConsent.settingsDialog.acceptAllBtn')"
      :cancel-btn-text="t('cookieConsent.settingsDialog.cancelBtn')"
      :save-preferences-btn-text="
        t('cookieConsent.settingsDialog.savePreferencesBtn')
      "
      @cancel="isCookieSettingsDialogVisible = false"
      @accept-all="acceptAllCookiesAndCloseSettings"
      @save-preferences="saveCookiePreferencesAndCloseSettings"
    >
      <template #settings-categories>
        <CookieConsentSettingsCategorySection
          v-for="section in cookieSettingsSections"
          :key="section.id"
          :title="t(section.title)"
          :description="t(section.description)"
          :icon-name="section.iconName"
          :name-label="t('cookieConsent.settingsDialog.labels.cookie')"
          :provider-label="t('cookieConsent.settingsDialog.labels.provider')"
          :purpose-label="t('cookieConsent.settingsDialog.labels.purpose')"
          :has-badge="section.hasBadge"
          :badge-text="section.badgeText ? t(section.badgeText) : ''"
          :has-toggle="section.hasToggle"
          :toggle-value="section.toggleValue"
          @toggle-category="section.handleToggleAction"
        >
          <template #category-details>
            <CookieConsentSettingsCategorySectionDetails
              v-for="(cookie, index) in section.cookies"
              :key="index"
              :name="cookie.name"
              :provider="cookie.provider"
              :purpose="t(cookie.purpose)"
            />
          </template>
        </CookieConsentSettingsCategorySection>
      </template>
    </CookieConsentSettingsDialog>
  </div>
</template>
<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useRuntimeConfig } from "nuxt/app";
import CookieConsentBanner from "~/components/shared/CookieConsent/CookieConsentBanner.vue";
import CookieConsentReadyToChatDialog from "~/components/shared/CookieConsent/CookieConsentReadyToChatDialog.vue";
import CookieConsentPrivacyRespectedDialog from "~/components/shared/CookieConsent/CookieConsentPrivacyRespectedDialog.vue";
import CookieConsentSettingsDialog from "~/components/shared/CookieConsent/CookieConsentSettingsDialog.vue";
import CookieConsentSettingsCategorySection from "~/components/shared/CookieConsent/CookieConsentSettingsCategorySection.vue";
import CookieConsentSettingsCategorySectionDetails from "~/components/shared/CookieConsent/CookieConsentSettingsCategorySectionDetails.vue";
import { useCookieConsentStore } from "~/store/cookieConsentStore";
const { t } = useI18n();
const config = useRuntimeConfig();
const cookieConsentStore = useCookieConsentStore();
const {
  isReadyToChatDialogVisible,
  isPrivacyRespectedDialogVisible,
  isCookieSettingsDialogVisible,
  hasRespondedToConsent,
  cookieSettingsSections,
} = storeToRefs(cookieConsentStore);
const {
  confirmChatAndEnableCookies,
  closeReadyToChatDialog,
  declineChatAndShowPrivacyRespectedDialog,
  openCookieSettingsDialog,
  acceptAllCookiesAndCloseSettings,
  saveCookiePreferencesAndCloseSettings,
  acceptEssentialCookiesOnly,
} = cookieConsentStore;
</script>
